import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Building2, Phone, LogOut, Lock, MapPin, Clock, Camera, Plus, X as XIcon, Check } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "../components/Logo";
import { adminLogin, adminSchedule, adminCreateJob, adminUpload, adminJobStatus } from "../lib/api";
import { planLabel, TIME_SLOTS } from "../lib/constants";

const TOKEN_KEY = "ews_admin_token";

const REFERENCE = {
  house: [
    "https://static.prod-images.emergentagent.com/jobs/cd28a7d7-b0c7-4042-82a2-33a900b3da2a/images/84ac79702e4acaa21dbc8d5285fe645ba5a597107b6e10a023a4a05d0012c953.jpeg",
    "https://static.prod-images.emergentagent.com/jobs/cd28a7d7-b0c7-4042-82a2-33a900b3da2a/images/b444e1394af4c27b67f2cd1ad48814709d05745659b6306be2fcdc4abe91a2eb.jpeg",
    "https://static.prod-images.emergentagent.com/jobs/cd28a7d7-b0c7-4042-82a2-33a900b3da2a/images/28e553f4131552063a6fa20183b3cac69790c531b8d21a8ce8a2a32cdf3c34e5.jpeg",
  ],
  store: [
    "https://static.prod-images.emergentagent.com/jobs/cd28a7d7-b0c7-4042-82a2-33a900b3da2a/images/79ea3528dab717405e787793bd96aeb82651398a36dbf0fe245c025c554b7a06.jpeg",
    "https://static.prod-images.emergentagent.com/jobs/cd28a7d7-b0c7-4042-82a2-33a900b3da2a/images/18d2a732c1861fd58619c42c4e757cf04b2cf655d64561f966349188862b27e2.jpeg",
    "https://static.prod-images.emergentagent.com/jobs/cd28a7d7-b0c7-4042-82a2-33a900b3da2a/images/f3db9834b1d67e6651ebb9bf917b6586c31765077055079199f56bfcb7547972.jpeg",
  ],
};

function LoginScreen({ onToken }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { token } = await adminLogin(password);
      sessionStorage.setItem(TOKEN_KEY, token);
      onToken(token);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-midnight flex items-center justify-center px-5">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm rounded-3xl glass-panel p-8"
        data-testid="team-login-card"
      >
        <div className="flex items-center gap-3 mb-8">
          <LogoMark size={38} />
          <div>
            <div className="font-display text-xl text-white leading-none">Everything Works</div>
            <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-crystal mt-1.5">Crew Schedule</div>
          </div>
        </div>
        <label className="font-mono text-[11px] tracking-[0.25em] uppercase text-mist block mb-3">Team password</label>
        <input
          data-testid="team-login-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••••"
          className="w-full rounded-xl bg-ink/80 border border-white/15 px-4 py-3.5 text-sm text-white outline-none focus:border-crystal focus:ring-1 focus:ring-crystal/50 transition-colors duration-200"
          autoFocus
        />
        {error && <p className="mt-2 text-xs text-red-400" data-testid="team-login-error">{error}</p>}
        <button
          data-testid="team-login-btn"
          type="submit"
          disabled={busy}
          className={`mt-6 w-full flex items-center justify-center gap-2 rounded-full py-4 font-mono text-[11px] font-semibold tracking-[0.25em] uppercase transition-all duration-200 ${
            busy ? "bg-white/10 text-mist cursor-wait" : "bg-crystal text-midnight hover:-translate-y-0.5"
          }`}
        >
          <Lock size={13} /> {busy ? "Checking…" : "Open schedule"}
        </button>
      </motion.form>
    </div>
  );
}

function QuickAddModal({ token, onClose, onSaved }) {
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [f, setF] = useState({
    customer_name: "", phone: "", address: "", date: tomorrow,
    time_slot: "9:00 AM", job_type: "house", notes: "",
  });
  const [photo, setPhoto] = useState(null); // storage path
  const [photoPreview, setPhotoPreview] = useState(null); // local blob for instant preview
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Photo must be under 10 MB");
      return;
    }
    setPhotoPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const { path } = await adminUpload(token, file);
      setPhoto(path);
      toast.success("Photo attached");
    } catch {
      toast.error("Upload failed — try again");
      setPhotoPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!f.customer_name.trim() || !f.address.trim() || !f.phone.trim()) {
      toast.error("Name, phone and address are required");
      return;
    }
    setSaving(true);
    try {
      const job = await adminCreateJob(token, { ...f, photos: photo ? [photo] : [] });
      toast.success(`${job.ref} added to the schedule`);
      onSaved();
      onClose();
    } catch {
      toast.error("Could not save the job — try again");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-ink/80 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-mist/60 outline-none focus:border-crystal focus:ring-1 focus:ring-crystal/50 transition-colors duration-200";

  return (
    <motion.div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="quickadd-modal">
      <div className="absolute inset-0 bg-midnight/85 backdrop-blur-md" onClick={onClose} data-testid="quickadd-backdrop" />
      <motion.div
        initial={{ y: 60, opacity: 0, scale: 0.98 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 40, opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-white/15 bg-[#0B0E14] p-6 sm:p-8"
        style={{ boxShadow: "0 60px 140px -40px rgba(0,0,0,1)" }}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-3xl text-white flex items-center gap-3">
            <Plus size={22} className="text-crystal" /> Quick add job
          </h3>
          <button data-testid="quickadd-close" onClick={onClose} className="rounded-full border border-white/15 p-2 text-mist hover:text-white hover:border-crystal transition-colors duration-200" aria-label="Close">
            <XIcon size={16} />
          </button>
        </div>

        <div className="space-y-4">
          <input data-testid="quickadd-name" placeholder="Customer name *" value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} className={inputCls} />
          <input data-testid="quickadd-phone" type="tel" placeholder="Phone number *" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} />
          <input data-testid="quickadd-address" placeholder="House or store address *" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className={inputCls} />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-mist mb-1.5">Date</div>
              <input data-testid="quickadd-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inputCls} />
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-mist mb-1.5">Arrival window</div>
              <select data-testid="quickadd-time" value={f.time_slot} onChange={(e) => setF({ ...f, time_slot: e.target.value })} className={inputCls}>
                {TIME_SLOTS.map((t) => <option key={t} value={t} className="bg-ink">{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-mist mb-1.5">Job type</div>
            <div className="grid grid-cols-2 gap-3">
              {[["house", "House"], ["store", "Store"]].map(([v, l]) => (
                <button key={v} data-testid={`quickadd-type-${v}`} onClick={() => setF({ ...f, job_type: v })} className={`rounded-xl border py-3 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors duration-200 ${f.job_type === v ? "border-crystal bg-crystal/10 text-crystal" : "border-white/15 text-mist hover:text-white"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Photo — camera on phones, file picker on desktop */}
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-mist mb-1.5">Picture of the place</div>
            <label
              data-testid="quickadd-photo-btn"
              className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-white/25 py-4 cursor-pointer hover:border-crystal hover:text-crystal transition-colors duration-200 text-mist text-sm"
            >
              <Camera size={16} />
              {uploading ? "Uploading…" : photo ? "Retake / replace photo" : "Take a photo"}
              <input data-testid="quickadd-photo-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={onPickPhoto} />
            </label>
            {photoPreview && (
              <img src={photoPreview} alt="Job preview" className="mt-3 w-full aspect-[4/3] object-cover rounded-xl border border-white/15" data-testid="quickadd-photo-preview" />
            )}
          </div>

          <textarea data-testid="quickadd-notes" rows={2} placeholder="Notes — gate code, dog, which windows…" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={`${inputCls} resize-none`} />

          <button
            data-testid="quickadd-save"
            onClick={save}
            disabled={saving || uploading}
            className={`w-full flex items-center justify-center gap-2 rounded-full py-4 font-mono text-[11px] font-semibold tracking-[0.25em] uppercase transition-all duration-200 ${
              saving || uploading ? "bg-white/10 text-mist cursor-wait" : "bg-crystal text-midnight hover:-translate-y-0.5"
            }`}
          >
            <Check size={14} /> {saving ? "Adding…" : "Add to schedule"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function JobCard({ job, token, onDone }) {
  const isHouse = job.job_type === "house";
  const photos = job.photos?.length
    ? job.photos.map((p) => `/api/files/${p}?auth=${token}`)
    : REFERENCE[isHouse ? "house" : "store"];
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5 }}
      className="rounded-2xl glass-panel p-6"
      data-testid={`team-job-${job.ref}`}
    >
      <div className="grid md:grid-cols-[1fr_220px] gap-6">
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-crystal/40 bg-crystal/10 px-4 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase text-crystal" data-testid={`team-type-${job.ref}`}>
              {isHouse ? <Home size={12} /> : <Building2 size={12} />}
              {isHouse ? "House window cleaning" : "Store window cleaning"}
            </span>
            <span className="inline-flex items-center gap-2 font-mono text-[11px] text-mist">
              <Clock size={12} /> {job.time_slot}
            </span>
            <span className="font-mono text-[10px] text-mist/60">{job.ref}</span>
          </div>

          <div className="flex items-start gap-2.5 text-white font-display text-2xl leading-snug" data-testid={`team-address-${job.ref}`}>
            <MapPin size={20} className="text-crystal shrink-0 mt-1" />
            {job.address}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] text-mist">
            <a href={`tel:${job.phone}`} className="flex items-center gap-2 text-white/90 hover:text-crystal transition-colors duration-200" data-testid={`team-call-${job.ref}`}>
              <Phone size={12} /> {job.name} · {job.phone}
            </a>
            {job.kind === "booking" && <span>{job.count} {job.unit}(s) · {job.scope === "exterior" ? "Exterior only" : "Inside & out"}</span>}
            {job.kind === "booking" && job.frequency !== "one_time" && <span className="text-crystal">{planLabel(job.frequency, job.plan_months)}</span>}
            {job.kind === "booking" && (job.addons_total || 0) > 0 && <span className="text-crystal">+${job.addons_total} extras</span>}
            {job.kind === "quick" && <span className="text-crystal">Added from the field</span>}
            {job.notes && <span className="italic text-mist/70 w-full">"{job.notes}"</span>}
          </div>

          {job.kind === "quick" && (
            <button
              data-testid={`team-done-${job.ref}`}
              onClick={() => onDone(job.ref)}
              className="mt-4 rounded-full border border-emerald-400/50 px-5 py-2 font-mono text-[10px] tracking-[0.15em] uppercase text-emerald-300 hover:bg-emerald-400/10 transition-colors duration-200"
            >
              Mark done
            </button>
          )}
        </div>

        <div>
          <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-mist mb-2">How the place looks</div>
          <div className="grid grid-cols-3 gap-2">
            {photos.slice(0, 3).map((src, i) => (
              <img
                key={src + i}
                src={src}
                alt={isHouse ? "House reference" : "Store reference"}
                className="w-full aspect-square object-cover rounded-lg border border-white/10"
                data-testid={`team-ref-${job.ref}-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Team() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const load = (t = token) => {
    if (!t) return;
    adminSchedule(t)
      .then(setJobs)
      .catch((err) => {
        if (err?.response?.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken(null);
        } else {
          setError("Could not load the schedule");
        }
      });
  };

  useEffect(() => {
    if (!token) return;
    adminSchedule(token)
      .then(setJobs)
      .catch((err) => {
        if (err?.response?.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken(null);
        } else {
          setError("Could not load the schedule");
        }
      });
  }, [token]);

  const markDone = async (ref) => {
    try {
      await adminJobStatus(token, ref, "done");
      toast.success(`${ref} done`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const byDate = useMemo(() => {
    if (!jobs) return [];
    const groups = {};
    jobs.forEach((j) => (groups[j.date] = groups[j.date] || []).push(j));
    return Object.entries(groups);
  }, [jobs]);

  if (!token) return <LoginScreen onToken={setToken} />;

  return (
    <div className="min-h-screen bg-midnight text-white font-body">
      <header className="sticky top-0 z-40 bg-midnight/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={30} />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-mist">Crew Schedule</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="team-view-site-link" className="hidden md:inline-flex font-mono text-[10px] tracking-[0.2em] uppercase text-mist hover:text-crystal transition-colors duration-200">
              View site
            </a>
            <button data-testid="team-logout-btn" onClick={() => { sessionStorage.removeItem(TOKEN_KEY); setToken(null); }} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-mist hover:text-white hover:border-crystal transition-colors duration-200">
              <LogOut size={12} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
          <h1 className="font-display font-medium text-[clamp(2rem,4vw,3rem)] text-white">
            Who we're seeing, <em className="italic text-crystal">and where.</em>
          </h1>
          <button
            data-testid="quickadd-open-btn"
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 rounded-full bg-crystal px-6 py-3 font-mono text-[11px] font-semibold tracking-[0.2em] uppercase text-midnight transition-transform duration-200 hover:-translate-y-0.5"
          >
            <Plus size={14} /> Quick add job
          </button>
        </div>
        <p className="text-mist text-sm mb-10">Every job: the address, what the place looks like, and the work.</p>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        {jobs && jobs.length === 0 && (
          <p className="font-mono text-xs text-mist" data-testid="team-empty">No upcoming jobs right now.</p>
        )}

        <div className="space-y-10">
          {byDate.map(([date, list]) => (
            <div key={date}>
              <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-crystal mb-4 border-b border-white/10 pb-2" data-testid={`team-date-${date}`}>
                {new Date(date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
              </div>
              <div className="space-y-4">
                {list.map((j) => <JobCard key={j.ref} job={j} token={token} onDone={markDone} />)}
              </div>
            </div>
          ))}
        </div>
      </main>

      <AnimatePresence>
        {showAdd && <QuickAddModal token={token} onClose={() => setShowAdd(false)} onSaved={() => load()} />}
      </AnimatePresence>
    </div>
  );
}
